import React, { useEffect, useMemo, useState } from "react";
import {
  createAdminMetalFrame,
  deleteAdminMetalFrame,
  getAdminMetalFrames,
  getAdminMetalFrameById,
  removeAdminMetalFrameImage,
  updateAdminMetalFrame,
  uploadAdminMetalFrameImage,
} from "../../../api/metalFramesAdmin";
import { useNotification } from "../../../context/NotificationContext";
import { resolveMediaUrl } from "../../../utils/media";
import { useTranslation } from "react-i18next";
import AdminProductPdfsPanel from "./AdminProductPdfsPanel";

const buildEmptyForm = (frameType) => ({
  frameKey: "",
  frameType,
  language: "en",
  name: "",
  heroTag: "METAL FRAMES",
  heroTitle: "",
  heroDescription: "",
  heroDescriptionMore: "",
  imageUrl: "",
  isActive: true,
  isCrossSection: false,
  diagramUrl: "",
  heroBullets: [{ text: "" }],
  standards: [{ text: "" }],
  materialProperties: [{ label: "", value: "" }],
  sectionDimensions: [{ label: "", value: "" }],
  dimensionsTable: [{ role: "", h1: "", width: "", thickness: "", length: "", zincCoating: "" }],
});

const mapFrameToForm = (frame, frameType) => ({
  frameKey: frame?.frameKey || "",
  frameType: frame?.frameType || frameType,
  language: String(frame?.language || "en").toLowerCase().split("-")[0],
  name: frame?.name || "",
  heroTag: frame?.heroTag || "METAL FRAMES",
  heroTitle: frame?.heroTitle || "",
  heroDescription: frame?.heroDescription || "",
  heroDescriptionMore: frame?.heroDescriptionMore || "",
  imageUrl: frame?.imageUrl || "",
  isActive: typeof frame?.isActive === "boolean" ? frame.isActive : true,
  isCrossSection: Boolean(frame?.isCrossSection),
  diagramUrl: frame?.diagramUrl || "",
  heroBullets: Array.isArray(frame?.heroBullets) ? frame.heroBullets.map((x) => ({ text: x?.text || "" })) : [{ text: "" }],
  standards: Array.isArray(frame?.standards) ? frame.standards.map((x) => ({ text: x?.text || "" })) : [{ text: "" }],
  materialProperties: Array.isArray(frame?.materialProperties)
    ? frame.materialProperties.map((x) => ({ label: x?.label || "", value: x?.value || "" }))
    : [{ label: "", value: "" }],
  sectionDimensions: Array.isArray(frame?.sectionDimensions)
    ? frame.sectionDimensions.map((x) => ({ label: x?.label || "", value: x?.value || "" }))
    : [{ label: "", value: "" }],
  dimensionsTable: Array.isArray(frame?.dimensionsTable)
    ? frame.dimensionsTable.map((x) => ({
        role: x?.role || "",
        h1: x?.h1 || "",
        width: x?.width || "",
        thickness: x?.thickness || "",
        length: x?.length || "",
        zincCoating: x?.zincCoating || "",
      }))
    : [{ role: "", h1: "", width: "", thickness: "", length: "", zincCoating: "" }],
});

const toPayload = (form, frameType) => ({
  frameKey: form.frameKey.trim(),
  frameType,
  language: String(form.language || "en").toLowerCase(),
  name: form.name.trim(),
  heroTag: form.heroTag.trim() || "METAL FRAMES",
  heroTitle: form.heroTitle.trim(),
  heroDescription: form.heroDescription.trim(),
  heroDescriptionMore: form.heroDescriptionMore.trim() || null,
  imageUrl: form.imageUrl.trim() || null,
  isActive: Boolean(form.isActive),
  isCrossSection: Boolean(form.isCrossSection),
  diagramUrl: form.diagramUrl.trim() || null,
  heroBullets: (form.heroBullets || []).map((x) => ({ text: x?.text || "" })).filter((x) => x.text),
  standards: (form.standards || []).map((x) => ({ text: x?.text || "" })).filter((x) => x.text),
  materialProperties: (form.materialProperties || [])
    .map((x) => ({ label: x?.label || "", value: x?.value || "" }))
    .filter((x) => x.label || x.value),
  sectionDimensions: (form.sectionDimensions || [])
    .map((x) => ({ label: x?.label || "", value: x?.value || "" }))
    .filter((x) => x.label || x.value),
  dimensionsTable: (form.dimensionsTable || [])
    .map((x) => ({
      role: x?.role || "",
      h1: x?.h1 || "",
      width: x?.width || "",
      thickness: x?.thickness || "",
      length: x?.length || "",
      zincCoating: x?.zincCoating || "",
    }))
    .filter((x) => x.role || x.h1 || x.width || x.thickness || x.length || x.zincCoating),
});

const MetalFramesManager = ({ frameType = "drywall-metal-frame", title = "Metal Frames" }) => {
  const { i18n } = useTranslation();
  const { notify } = useNotification();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(buildEmptyForm(frameType));
  const [createImageFile, setCreateImageFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(buildEmptyForm(frameType));
  const [editImageFile, setEditImageFile] = useState(null);
  const [uploadFiles, setUploadFiles] = useState({});

  useEffect(() => {
    setCreateForm(buildEmptyForm(frameType));
    setEditForm(buildEmptyForm(frameType));
    setEditingId(null);
  }, [frameType]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await getAdminMetalFrames({ frameType });
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({ type: "error", title: "Failed to load metal frames", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setLoading(false);
    }
  };

  const refreshMetalFrameRowById = async (id) => {
    if (id == null) {
      await loadItems();
      return;
    }
    try {
      const one = await getAdminMetalFrameById(id);
      setItems((prev) => {
        const idx = prev.findIndex((x) => Number(x.id) === Number(id));
        if (idx === -1) return one ? [...prev, one] : prev;
        const next = [...prev];
        next[idx] = one;
        return next;
      });
    } catch {
      await loadItems();
    }
  };

  useEffect(() => {
    loadItems();
  }, [frameType]);

  const sortedItems = useMemo(() => {
    const currentLang = String(i18n.language || "en").toLowerCase().split("-")[0];
    const filtered = items.filter((item) => {
      const itemLang = String(item?.language || "en").toLowerCase().split("-")[0];
      const itemType = String(item?.frameType || "").toLowerCase();
      return itemLang === currentLang && itemType === String(frameType).toLowerCase();
    });
    return [...filtered].sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));
  }, [items, i18n.language, frameType]);

  const editingItem = useMemo(
    () => items.find((x) => Number(x?.id) === Number(editingId)) || null,
    [items, editingId]
  );

  const handleCreate = async () => {
    if (!createForm.frameKey.trim() || !createForm.name.trim() || !createForm.heroTitle.trim()) {
      notify({ type: "error", title: "Frame key, Name and Hero Title are required" });
      return;
    }
    setCreating(true);
    try {
      const created = await createAdminMetalFrame(toPayload(createForm, frameType));
      if (createImageFile && created?.id) {
        await uploadAdminMetalFrameImage({ id: created.id, file: createImageFile });
      }
      notify({ type: "success", title: "Metal frame created" });
      setCreateForm(buildEmptyForm(frameType));
      setCreateImageFile(null);
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Create failed", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = async (item) => {
    setEditingId(item.id);
    setEditForm(mapFrameToForm(item, frameType));
    setEditImageFile(null);
    try {
      const fresh = await getAdminMetalFrameById(item.id);
      setItems((prev) => prev.map((x) => (Number(x.id) === Number(item.id) ? fresh : x)));
      setEditForm(mapFrameToForm(fresh, frameType));
    } catch {
      /* keep row from list */
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    if (!editForm.frameKey.trim() || !editForm.name.trim() || !editForm.heroTitle.trim()) {
      notify({ type: "error", title: "Frame key, Name and Hero Title are required" });
      return;
    }
    try {
      await updateAdminMetalFrame({ id: editingId, payload: toPayload(editForm, frameType) });
      if (editImageFile) {
        await uploadAdminMetalFrameImage({ id: editingId, file: editImageFile });
      }
      notify({ type: "success", title: "Metal frame updated" });
      setEditingId(null);
      setEditForm(buildEmptyForm(frameType));
      setEditImageFile(null);
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Update failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete metal frame "${item?.name || item?.heroTitle || item?.frameKey}"?`)) return;
    try {
      await deleteAdminMetalFrame({ id: item.id });
      notify({ type: "success", title: "Metal frame deleted" });
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Delete failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const renderPairRows = (rows, setRowsDirect, labels = ["Label", "Value"]) => (
    <>
      {rows.map((row, idx) => (
        <div key={idx} className="grid sm:grid-cols-12 gap-2 mb-2">
          <input type="text" value={row?.label || ""} onChange={(e) => {
            const next = [...rows];
            next[idx] = { ...(next[idx] || {}), label: e.target.value };
            setRowsDirect(next);
          }} placeholder={labels[0]} className="sm:col-span-5 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <input type="text" value={row?.value || ""} onChange={(e) => {
            const next = [...rows];
            next[idx] = { ...(next[idx] || {}), value: e.target.value };
            setRowsDirect(next);
          }} placeholder={labels[1]} className="sm:col-span-6 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <button type="button" onClick={() => {
            const next = rows.filter((_, i) => i !== idx);
            setRowsDirect(next.length ? next : [{ label: "", value: "" }]);
          }} className="px-3 rounded border border-rose-200 text-rose-700">-</button>
        </div>
      ))}
    </>
  );

  const renderForm = ({ form, setForm, imageSetter, imageFile, onUploadImage, onRemoveImage }) => (
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="text-sm text-slate-700">Frame Key
        <input type="text" value={form.frameKey} onChange={(e) => setForm((p) => ({ ...p, frameKey: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700">Language
        <select value={form.language} onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
          <option value="en">EN</option><option value="ar">AR</option><option value="ku">KU</option>
        </select>
      </label>
      <label className="text-sm text-slate-700">Name
        <input type="text" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700">Hero Tag
        <input type="text" value={form.heroTag} onChange={(e) => setForm((p) => ({ ...p, heroTag: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">Hero Title
        <input type="text" value={form.heroTitle} onChange={(e) => setForm((p) => ({ ...p, heroTitle: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">Hero Description
        <textarea value={form.heroDescription} onChange={(e) => setForm((p) => ({ ...p, heroDescription: e.target.value }))} className="mt-1 w-full min-h-[80px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">Hero Description More
        <textarea value={form.heroDescriptionMore} onChange={(e) => setForm((p) => ({ ...p, heroDescriptionMore: e.target.value }))} className="mt-1 w-full min-h-[70px] rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="text-sm text-slate-700">Image URL
        <input type="text" value={form.imageUrl} onChange={(e) => setForm((p) => ({ ...p, imageUrl: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
      </label>
      <label className="inline-flex items-center gap-2 text-sm text-slate-700 mt-6">
        <input type="checkbox" checked={Boolean(form.isActive)} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
        Active
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">Section diagram URL (only when cross-section is on)
        <input
          type="text"
          value={form.diagramUrl}
          onChange={(e) => setForm((p) => ({ ...p, diagramUrl: e.target.value }))}
          placeholder="/uploads/... or https://..."
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <label className="inline-flex items-start gap-2 text-sm text-slate-700 sm:col-span-2">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={Boolean(form.isCrossSection)}
          onChange={(e) => setForm((p) => ({ ...p, isCrossSection: e.target.checked }))}
        />
        <span>
          Cross-section: show diagram beside material properties on the public product page when diagram URL is set.
        </span>
      </label>
      {imageSetter ? (
        <label className="text-sm text-slate-700 sm:col-span-2">Image File (optional)
          <input type="file" accept="image/*" onChange={(e) => imageSetter(e.target.files?.[0] || null)} className="mt-1 block w-full text-sm" />
          {imageFile?.name ? <span className="mt-1 block text-xs text-slate-500">Selected: {imageFile.name}</span> : null}
        </label>
      ) : null}
      {onUploadImage || onRemoveImage ? (
        <div className="sm:col-span-2 flex items-center gap-2">
          {onUploadImage ? <button type="button" onClick={onUploadImage} disabled={!imageFile} className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-60">Upload/Replace image</button> : null}
          {onRemoveImage ? <button type="button" onClick={onRemoveImage} className="text-xs px-3 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50">Remove image</button> : null}
        </div>
      ) : null}

      <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
        <p className="text-sm font-semibold text-[#011337] mb-2">Hero Bullets</p>
        {form.heroBullets.map((row, idx) => (
          <div key={idx} className="flex gap-2 mb-2">
            <input type="text" value={row?.text || ""} onChange={(e) => setForm((p) => { const next = [...p.heroBullets]; next[idx] = { text: e.target.value }; return { ...p, heroBullets: next }; })} className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <button type="button" onClick={() => setForm((p) => {
              const next = p.heroBullets.filter((_, i) => i !== idx);
              return { ...p, heroBullets: next.length ? next : [{ text: "" }] };
            })} className="px-3 rounded border border-rose-200 text-rose-700">-</button>
          </div>
        ))}
        <button type="button" onClick={() => setForm((p) => ({ ...p, heroBullets: [...p.heroBullets, { text: "" }] }))} className="text-xs px-3 py-1.5 rounded border border-slate-300">Add bullet</button>
      </div>

      <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
        <p className="text-sm font-semibold text-[#011337] mb-2">Standards</p>
        {form.standards.map((row, idx) => (
          <div key={idx} className="flex gap-2 mb-2">
            <input type="text" value={row?.text || ""} onChange={(e) => setForm((p) => { const next = [...p.standards]; next[idx] = { text: e.target.value }; return { ...p, standards: next }; })} className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <button type="button" onClick={() => setForm((p) => {
              const next = p.standards.filter((_, i) => i !== idx);
              return { ...p, standards: next.length ? next : [{ text: "" }] };
            })} className="px-3 rounded border border-rose-200 text-rose-700">-</button>
          </div>
        ))}
        <button type="button" onClick={() => setForm((p) => ({ ...p, standards: [...p.standards, { text: "" }] }))} className="text-xs px-3 py-1.5 rounded border border-slate-300">Add standard</button>
      </div>

      <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
        <p className="text-sm font-semibold text-[#011337] mb-2">Material Properties</p>
        {renderPairRows(form.materialProperties, (next) => setForm((p) => ({ ...p, materialProperties: next })))}
        <button type="button" onClick={() => setForm((p) => ({ ...p, materialProperties: [...p.materialProperties, { label: "", value: "" }] }))} className="text-xs px-3 py-1.5 rounded border border-slate-300">Add property</button>
      </div>

      <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
        <p className="text-sm font-semibold text-[#011337] mb-2">Section Dimensions</p>
        {renderPairRows(form.sectionDimensions, (next) => setForm((p) => ({ ...p, sectionDimensions: next })))}
        <button type="button" onClick={() => setForm((p) => ({ ...p, sectionDimensions: [...p.sectionDimensions, { label: "", value: "" }] }))} className="text-xs px-3 py-1.5 rounded border border-slate-300">Add section dimension</button>
      </div>

      <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
        <p className="text-sm font-semibold text-[#011337] mb-2">Dimensions Table</p>
        {form.dimensionsTable.map((row, idx) => (
          <div key={idx} className="grid sm:grid-cols-6 gap-2 mb-2">
            {["role", "h1", "width", "thickness", "length", "zincCoating"].map((field) => (
              <input key={field} type="text" value={row?.[field] || ""} onChange={(e) =>
                setForm((p) => {
                  const next = [...p.dimensionsTable];
                  next[idx] = { ...(next[idx] || {}), [field]: e.target.value };
                  return { ...p, dimensionsTable: next };
                })
              } placeholder={field} className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            ))}
            <button type="button" onClick={() => setForm((p) => {
              const next = p.dimensionsTable.filter((_, i) => i !== idx);
              return { ...p, dimensionsTable: next.length ? next : [{ role: "", h1: "", width: "", thickness: "", length: "", zincCoating: "" }] };
            })} className="sm:col-span-6 px-3 py-1.5 rounded border border-rose-200 text-rose-700 text-xs">Remove row</button>
          </div>
        ))}
        <button type="button" onClick={() => setForm((p) => ({ ...p, dimensionsTable: [...p.dimensionsTable, { role: "", h1: "", width: "", thickness: "", length: "", zincCoating: "" }] }))} className="text-xs px-3 py-1.5 rounded border border-slate-300">Add table row</button>
      </div>
    </div>
  );

  return (
    <section className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#011337]">{title} Manager</h2>
            <p className="text-sm text-slate-500 mt-1">CRUD {title.toLowerCase()} and manage images.</p>
          </div>
          <button type="button" onClick={loadItems} className="px-4 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm">Reload</button>
        </div>
        {renderForm({ form: createForm, setForm: setCreateForm, imageSetter: setCreateImageFile, imageFile: createImageFile })}
        <div className="mt-3 flex justify-end">
          <button type="button" disabled={creating} onClick={handleCreate} className="px-4 py-2 rounded-lg bg-[#FE7E3F] text-white hover:bg-[#FE7E3F]/90 text-sm disabled:opacity-60">
            {creating ? "Creating..." : "Create item"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">ID</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Key</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Lang</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Image</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Upload/Remove</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-4 text-sm text-slate-500">Loading items...</td></tr>
            ) : sortedItems.length ? (
              sortedItems.map((item) => {
                const image = resolveMediaUrl(item?.imageUrl);
                return (
                  <tr key={item.id} className="border-b border-slate-200 align-top">
                    <td className="px-3 py-3 text-sm text-slate-700">{item.id}</td>
                    <td className="px-3 py-3 text-sm text-slate-700">{item.frameKey || "-"}</td>
                    <td className="px-3 py-3 text-sm text-slate-700 uppercase">{item.language || "-"}</td>
                    <td className="px-3 py-3 text-sm font-medium text-slate-900">{item.name || item.heroTitle || "-"}</td>
                    <td className="px-3 py-3">{image ? <img src={image} alt="" className="w-24 h-16 object-cover rounded border border-slate-200" /> : <p className="text-xs text-slate-500">No image</p>}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-2">
                        <input type="file" accept="image/*" onChange={(e) => setUploadFiles((p) => ({ ...p, [item.id]: e.target.files?.[0] || null }))} className="text-xs" />
                        <div className="flex gap-2">
                          <button type="button" disabled={!uploadFiles[item.id]} onClick={async () => {
                            try {
                              await uploadAdminMetalFrameImage({ id: item.id, file: uploadFiles[item.id] });
                              notify({ type: "success", title: "Image uploaded" });
                              await loadItems();
                            } catch (error) {
                              notify({ type: "error", title: "Upload failed", message: error?.response?.data?.message || error?.message || "" });
                            }
                          }} className="text-xs px-2 py-1.5 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-60">Upload</button>
                          <button type="button" onClick={async () => {
                            try {
                              await removeAdminMetalFrameImage({ id: item.id });
                              notify({ type: "success", title: "Image removed" });
                              await loadItems();
                            } catch (error) {
                              notify({ type: "error", title: "Remove failed", message: error?.response?.data?.message || error?.message || "" });
                            }
                          }} className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50">Remove</button>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-2">
                        <button type="button" onClick={() => startEdit(item)} className="text-xs px-2 py-1.5 rounded border border-[#011337] text-[#011337] hover:bg-[#011337]/10">Edit</button>
                        <button type="button" onClick={() => handleDelete(item)} className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50">Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan={7} className="px-3 py-4 text-sm text-slate-500">No items found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editingId ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-lg font-semibold text-[#011337]">Edit Item #{editingId}</h3>
            <button type="button" onClick={() => {
              setEditingId(null);
              setEditForm(buildEmptyForm(frameType));
              setEditImageFile(null);
            }} className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50">Close</button>
          </div>
          {renderForm({
            form: editForm,
            setForm: setEditForm,
            imageSetter: setEditImageFile,
            imageFile: editImageFile,
            onUploadImage: editingId ? async () => {
              if (!editImageFile) return;
              try {
                await uploadAdminMetalFrameImage({ id: editingId, file: editImageFile });
                notify({ type: "success", title: "Image uploaded" });
                setEditImageFile(null);
                await loadItems();
              } catch (error) {
                notify({ type: "error", title: "Upload failed", message: error?.response?.data?.message || error?.message || "" });
              }
            } : null,
            onRemoveImage: editingId ? async () => {
              try {
                await removeAdminMetalFrameImage({ id: editingId });
                notify({ type: "success", title: "Image removed" });
                await loadItems();
              } catch (error) {
                notify({ type: "error", title: "Remove failed", message: error?.response?.data?.message || error?.message || "" });
              }
            } : null,
          })}
          <AdminProductPdfsPanel
            variant="metalFrame"
            productId={editingId}
            productSlug={String(editingItem?.frameKey ?? editForm?.frameKey ?? "").trim()}
            pdfDocuments={editingItem?.pdfDocuments || editingItem?.pdfs || []}
            onRefresh={() => refreshMetalFrameRowById(editingId)}
          />
          <div className="mt-3 flex justify-end">
            <button type="button" onClick={saveEdit} className="px-4 py-2 rounded-lg bg-[#011337] text-white hover:bg-[#011337]/90 text-sm">Save changes</button>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default MetalFramesManager;
